import React, { createContext, useCallback, useContext, useState } from 'react';

const ToastContext = createContext(null);

let _addToast = null;

/**
 * Call toast() anywhere in the app — no hook required.
 * toast('Saved!') | toast('Error', 'error') | toast('Heads up', 'warning')
 */
export const toast = (message, type = 'success', duration = 4000) => {
    if (_addToast) _addToast(message, type, duration);
};

export const useToast = () => useContext(ToastContext);

const ICONS = {
    success: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),
    error: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
    warning: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    info: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),
};

const COLORS = {
    success: { bg: '#f0fdf4', border: '#86efac', icon: '#16a34a', text: '#15803d' },
    error:   { bg: '#fef2f2', border: '#fca5a5', icon: '#dc2626', text: '#b91c1c' },
    warning: { bg: '#fffbeb', border: '#fcd34d', icon: '#d97706', text: '#b45309' },
    info:    { bg: '#eff6ff', border: '#93c5fd', icon: '#2563eb', text: '#1d4ed8' },
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'success', duration = 4000) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }, []);

    _addToast = addToast;

    const remove = (id) => setToasts(prev => prev.filter(t => t.id !== id));

    return (
        <ToastContext.Provider value={addToast}>
            {children}
            <div style={{
                position: 'fixed', bottom: 24, right: 24, zIndex: 99999,
                display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380,
            }}>
                {toasts.map(t => {
                    const c = COLORS[t.type] || COLORS.info;
                    return (
                        <div key={t.id} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            background: c.bg, border: `1px solid ${c.border}`,
                            borderRadius: 10, padding: '12px 14px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                            animation: 'toastIn 0.25s ease',
                            minWidth: 280,
                        }}>
                            <span style={{ color: c.icon, flexShrink: 0, marginTop: 1 }}>{ICONS[t.type]}</span>
                            <span style={{ flex: 1, fontSize: 14, color: '#1f2937', lineHeight: '1.4' }}>{t.message}</span>
                            <button onClick={() => remove(t.id)} style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#9ca3af', fontSize: 16, padding: 0, lineHeight: 1, flexShrink: 0,
                            }}>×</button>
                        </div>
                    );
                })}
            </div>
            <style>{`@keyframes toastIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </ToastContext.Provider>
    );
};
